import type { EnhancedScopeData } from '@ardoq/data-model';
import {
  type AlignSide,
  type ResolvedArrangement,
  type ResolvedRegion,
  sameReferenceType,
} from './compileArrangement';
import {
  type ArrangeableTreeNode,
  findNode,
  toArrangeableNodes,
} from './arrangeableNodes';

/** Columns when one group sits above or below the other, rows when they sit side by side. */
export type GroupLinkAxis = 'col' | 'row';

/**
 * A group an alignWith lines up with the only block of the region it names, itself a group. The
 * blocks inside the source group line up with the blocks they relate to inside the target group.
 */
export type GroupLink = {
  /** The group whose arrangement holds the two regions. */
  containerId: string | null;
  sourceGroupId: string;
  targetGroupId: string;
  /** Which side of the target group the source group sits on. */
  side: AlignSide;
  /**
   * For each child of the source group, the children of the target group it relates to, direct
   * references before references into their contents. Children that relate to none are left out.
   */
  relatedTargets: Record<string, string[]>;
};

type LinkedRegion = {
  side: AlignSide;
  referenceTypes: string[];
  sourceId: string;
  targetId: string;
  sourceChildren: ArrangeableTreeNode[];
  targetChildren: ArrangeableTreeNode[];
};

const toLinkedRegions = (
  root: ArrangeableTreeNode,
  { region, nodes, alignment }: ResolvedRegion
): LinkedRegion[] => {
  const { alignWith } = region;
  if (!alignment || !alignWith || alignment.targetNodes.length !== 1) return [];
  const [targetNode] = alignment.targetNodes;
  const target = targetNode.isGroup ? findNode(root, targetNode.id) : null;
  const targetChildren = target?.children;
  if (!target || !targetChildren) return [];
  return nodes.flatMap(sourceNode => {
    const source = sourceNode.isGroup ? findNode(root, sourceNode.id) : null;
    const sourceChildren = source?.children;
    return source && sourceChildren
      ? [
          {
            side: alignment.side,
            referenceTypes: alignWith.referenceTypes,
            sourceId: source.id,
            targetId: target.id,
            sourceChildren,
            targetChildren,
          },
        ]
      : [];
  });
};

const toRelatedTargets = (
  scopeData: EnhancedScopeData,
  { referenceTypes, sourceChildren, targetChildren }: LinkedRegion
): Record<string, string[]> => {
  const targetIds = new Set(targetChildren.map(({ id }) => id));
  const sourceIds = new Set(sourceChildren.map(({ id }) => id));
  const nodes = toArrangeableNodes(scopeData, [
    ...sourceChildren,
    ...targetChildren,
  ]);
  return Object.fromEntries(
    nodes
      .filter(({ id }) => sourceIds.has(id))
      .flatMap(node => {
        const related = node.related
          .filter(
            ({ nodeId, referenceType }) =>
              targetIds.has(nodeId) &&
              sameReferenceType(referenceTypes, referenceType)
          )
          .sort((a, b) => Number(a.nested) - Number(b.nested))
          .map(({ nodeId }) => nodeId);
        const unique = [...new Set(related)];
        return unique.length ? [[node.id, unique] as const] : [];
      })
  );
};

export const toAxis = (side: AlignSide): GroupLinkAxis =>
  side === 'left' || side === 'right' ? 'row' : 'col';

type ToGroupLinksArgs = {
  scopeData: EnhancedScopeData;
  root: ArrangeableTreeNode;
  resolved: ResolvedArrangement[];
};

/** Every group these arrangements line up with a sibling group it relates to. */
export const toGroupLinks = ({
  scopeData,
  root,
  resolved,
}: ToGroupLinksArgs): GroupLink[] =>
  resolved.flatMap(({ arrangement, regions }) =>
    regions
      .flatMap(region => toLinkedRegions(root, region))
      .map(linked => ({
        containerId: arrangement.containerId,
        sourceGroupId: linked.sourceId,
        targetGroupId: linked.targetId,
        side: linked.side,
        relatedTargets: toRelatedTargets(scopeData, linked),
      }))
      .filter(({ relatedTargets }) => Object.keys(relatedTargets).length > 0)
  );
